import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlayerPause, TbPlayerPlay, TbReload, TbVideoOff } from "react-icons/tb";

import "@/assets/NodePlayer/NodePlayer.d.ts";
import PlateComponent from "@/components/Plate";
import { useGetDriverCameras } from "@/requests/transport.queries";
import { DriverType } from "@/types/driver.types";

import "./camera.css";

// Driven by the page's "enter" / "center" variants, so cards appear one after another (staggered).
const cardVariants = {
    enter: { opacity: 0, y: 16, scale: 0.97 },
    center: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const } },
};

const overlayMotion = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.2 },
};

const CameraSkeleton = () => {
    return (
        <div className="col-span-1 min-h-0 flex-1 bg-slate-900 rounded-xl flex justify-center items-center">
            <CircularProgress size={"1.5rem"} sx={{ color: "rgb(148 163 184)" }} />
        </div>
    );
};

const CameraNotFound = () => {
    return (
        <div className="col-span-1 min-h-0 flex-1 bg-slate-900 rounded-xl flex flex-col gap-1 justify-center items-center text-slate-400 text-sm">
            <TbVideoOff />
            Camera not found
        </div>
    );
};
const CameraRender = ({ url, id, label }: { url: string; id: string; label: string }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const [player, setPlayer] = useState<NodePlayer | null>(null);

    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const player = new NodePlayer();
        player.setView(id, true);
        player.setBufferTime(100);
        player.setVolume(0);
        player.enableAudio(false);

        player.on("buffer", (state) => {
            if (state === "full") setLoading(false);
        });
        player.setTimeout(10);
        player.on("timeout", () => {
            player.stop();
            setPlaying(false);
        });

        setPlayer(player);

        player.start(url);
        setPlaying(true);
        setLoading(true);

        return () => {
            player.stop();
            player.release();
        };
    }, [url, id]);

    return (
        <div
            key={url}
            className="relative col-span-1 min-h-0 flex-1 overflow-hidden player-styles bg-slate-900 rounded-xl flex flex-col gap-1 justify-center items-center camera-fit"
        >
            <canvas className="absolute inset-0 h-full w-full object-contain" id={id}></canvas>
            <div className="absolute top-2 left-2 z-10 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                {label}
            </div>
            <AnimatePresence>
                {playing && !loading && (
                    <motion.div
                        key="live"
                        {...overlayMotion}
                        className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                        </span>
                        {t("live")}
                    </motion.div>
                )}
                {playing && !loading && (
                    <motion.div
                        key="pause"
                        {...overlayMotion}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                            player?.stop();
                            setPlaying(false);
                        }}
                        className="absolute bottom-2 left-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
                    >
                        <TbPlayerPause />
                    </motion.div>
                )}
            </AnimatePresence>
            <div
                className={`absolute mt-2 ${playing ? "cursor-pointer" : "cursor-pointer"}`}
                onClick={() => {
                    if (!playing) {
                        player?.start(url);
                        setPlaying(true);
                    } else {
                        player?.pause(true);
                        setPlaying(false);
                    }
                }}
            >
                {!playing && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                        className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70"
                    >
                        <TbPlayerPlay color="white" size={24} />
                    </motion.div>
                )}
                {playing && loading && <CircularProgress color="warning" size={"1.5rem"} />}
            </div>
        </div>
    );
};

const DriverCamera = ({ driver, licensePlate }: { driver: DriverType; licensePlate?: string }) => {
    const { t } = useTranslation();
    const { data: cameras = [], refetch, isRefetching, isLoading } = useGetDriverCameras(driver.vehicle?.id || "");
    const [refreshed, setRefreshed] = useState(0);

    return (
        <motion.div
            variants={cardVariants}
            className="col-span-1 flex h-full min-h-0 flex-col gap-2 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-2 shadow-sm transition-shadow hover:shadow-md"
        >
            <div className="flex shrink-0 items-center justify-between gap-2 px-1">
                <div className="flex min-w-0 items-center gap-3">
                    {licensePlate && <PlateComponent plate={licensePlate} className="bg-white" />}
                    <span className="truncate text-sm font-medium text-slate-700">{driver.name}</span>
                </div>
                <Tooltip title={t("refresh")}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            refetch();
                            setRefreshed(refreshed + 1);
                        }}
                    >
                        {isRefetching ? <CircularProgress size={"1rem"} /> : <TbReload />}
                    </IconButton>
                </Tooltip>
            </div>

            <div key={refreshed} className="flex min-h-0 flex-1 flex-col gap-2">
                {isLoading ? (
                    <CameraSkeleton />
                ) : cameras[0] ? (
                    <CameraRender url={cameras[0]} id={`cam-${driver.id}-0`} label={`${t("camera")} 1`} />
                ) : (
                    <CameraNotFound />
                )}

                {isLoading ? (
                    <CameraSkeleton />
                ) : cameras[1] ? (
                    <CameraRender url={cameras[1]} id={`cam-${driver.id}-1`} label={`${t("camera")} 2`} />
                ) : (
                    <CameraNotFound />
                )}
            </div>
        </motion.div>
    );
};
export default DriverCamera;
